// Tab-scoped preferences only: never store names, contact details or intake text.
// Reads are non-destructive for StrictMode and refresh; stale entries expire.
import { normalizeClient } from './matchingPreferences.js';

const PREFIX = 'plw_journey_';
export const JOURNEY_TTL = 30 * 60 * 1000;
const memory = new Map();
const timers = new Map();
function scheduleExpiry(key, expires) {
  if (typeof window?.setTimeout === 'function') {
    if (timers.has(key)) window.clearTimeout(timers.get(key));
    timers.set(key, window.setTimeout(() => clearJourney(key), Math.max(0, expires - Date.now())));
  }
}
export function writeJourney(key, value) {
  if (typeof window === 'undefined') return;
  const entry = { expires: Date.now() + JOURNEY_TTL, value };
  memory.set(key, entry);
  scheduleExpiry(key, entry.expires);
  listenForJourneyRequests();
  try { window.sessionStorage.setItem(PREFIX + key, JSON.stringify(entry)); } catch { /* in-memory fallback */ }
}
export function readJourney(key) {
  if (typeof window === 'undefined') return null;
  let entry;
  try { entry = JSON.parse(window.sessionStorage.getItem(PREFIX + key) || 'null'); } catch { /* unavailable/corrupt */ }
  entry ||= memory.get(key);
  if (!entry || typeof entry.expires !== 'number' || entry.expires <= Date.now()) { clearJourney(key); return null; }
  scheduleExpiry(key, entry.expires);
  return entry.value && typeof entry.value === 'object' && !Array.isArray(entry.value) ? entry.value : null;
}
export function clearJourney(key) {
  memory.delete(key);
  if (typeof window !== 'undefined' && timers.has(key)) window.clearTimeout(timers.get(key));
  timers.delete(key);
  if (typeof window !== 'undefined') {
    try { window.sessionStorage.removeItem(PREFIX + key); } catch { /* unavailable */ }
  }
}
export function readSelection(search) {
  if (typeof window === 'undefined') return null;
  const id = new URLSearchParams(search ?? window.location.search).get('selection');
  return id && /^[a-z0-9-]{20,64}$/i.test(id) ? readJourney(`selection_${id}`) : null;
}
export function selectionUrl(to, value) {
  const id = window.crypto.randomUUID();
  writeJourney(`selection_${id}`, { client: normalizeClient(value.client),
    ...(typeof value.requestedTherapist === 'string' && { requestedTherapist: value.requestedTherapist }),
    source: value.source === 'directory' ? 'directory' : 'get-matched', humanHelp: value.humanHelp === true });
  const url = new URL(to, window.location.origin);
  url.searchParams.set('selection', id);
  return `${url.pathname}${url.search}${url.hash}`;
}
export function clearSelection() {
  if (typeof window === 'undefined') return;
  const keys = new Set(memory.keys());
  try {
    for (let index = 0; index < window.sessionStorage.length; index++) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PREFIX)) keys.add(key.slice(PREFIX.length));
    }
  } catch { /* unavailable */ }
  for (const key of keys) if (key.startsWith('selection_')) clearJourney(key);
  clearJourney('matching');
  clearJourney('directory');
}

// New tabs do not reliably inherit sessionStorage. Transfer only the specific
// opaque selection requested, locally between same-origin tabs; never a server.
let channel;
export function listenForJourneyRequests() {
  if (typeof window === 'undefined' || !window.BroadcastChannel) return null;
  if (!channel) {
    channel = new window.BroadcastChannel('plw-selection-handoff');
    channel.addEventListener('message', ({ data }) => {
      if (data?.type !== 'request' || !/^[a-z0-9-]{20,64}$/i.test(data.id)) return;
      const value = readJourney(`selection_${data.id}`);
      if (value) channel.postMessage({ type: 'response', id: data.id, request: data.request, value });
    });
  }
  return channel;
}
export async function recoverSelection(search) {
  const existing = readSelection(search);
  if (existing || typeof window === 'undefined') return existing;
  const id = new URLSearchParams(search ?? window.location.search).get('selection');
  const localChannel = listenForJourneyRequests();
  if (!id || !/^[a-z0-9-]{20,64}$/i.test(id) || !localChannel) return null;
  return new Promise((resolve) => {
    const request = window.crypto.randomUUID();
    const finish = (value) => {
      window.clearTimeout(timeout);
      localChannel.removeEventListener('message', receive);
      if (value) writeJourney(`selection_${id}`, value);
      resolve(value);
    };
    const receive = ({ data }) => {
      if (data?.type === 'response' && data.id === id && data.request === request) finish(data.value);
    };
    const timeout = window.setTimeout(() => finish(null), 500);
    localChannel.addEventListener('message', receive);
    localChannel.postMessage({ type: 'request', id, request });
  });
}
